<template>
  <div
    v-editable="blok"
    class="CategoryListingBlock layout-container"
    :class="[desktopWidth('full'), blockPaddingTop('md'), blockPaddingBottom('md')]"
  >
    <div>
      <div
        v-if="blok.headingTitle || blok.headingButtonText"
        class="flex flex-row flex-wrap justify-between items-center gap-8 mb-16"
        :class="{'flex-col text-center' : blok.headingAlign === 'center'}"
      >
        <div class="type-headline-xl"> {{ blok.headingTitle }}</div>
        <Nuxt-link
          :to="$sbLinkFix(blok.headingButtonLink)"
          :target="$sbLinkTarget(blok.headingButtonLink)"
        >
          <div
            v-if="blok.headingButtonText"
            class="btn btn--primary"
            :class="{
              'btn--secondary': blok.headingButtonStyle === 'secondary',
              'btn--underline': blok.headingButtonStyle === 'underline',
            }"
          >
            {{ blok.headingButtonText }}
          </div>
        </Nuxt-link>
      </div>
      <div
        v-if="blok.design === 'buttons'"
        class="flex flex-row flex-wrap justify-center items-center gap-8"
      >
        <nuxt-link
          v-for="item in blok.listItems"
          :key="item._uid"
          :to="itemLink(item)"
          class="shrink-0"
        >
          <div
            class="
            flex justify-center items-center type-headline-lg h-40 desk:h-56 px-16 desk:px-24 rounded-2 border
          border-border-inverse hover:bg-bg-inverse hover:text-text-inverse transition duration-300 ease-in-out cursor-pointer"
          >
            {{ item.category }}
          </div>
        </nuxt-link>
      </div>
      <component
        :is="uiStore.isMobile ? SideScroll:'div'"
        v-if="blok.design === 'buttons-swiper'"
      >
        <div class="flex gap-12 mobOnly:pl-12 desk:flex-wrap">
          <nuxt-link
            v-for="item in blok.listItems"
            :key="item._uid"
            :to="itemLink(item)"
            class="shrink-0"
          >
            <div class="btn btn--secondary"> {{ item.category }}</div>
          </nuxt-link>

          <div class="sideScrollRightPad desk:hidden" />
        </div>
      </component>
      <div
        v-if="(blok.swiperMobile && isMobile) || (itemCount > 6)"
        class="relative"
        :class="{
          'hidden' : blok.design === 'buttons',
        }"
      >
        <client-only>
          <Swiper
            class="swiper-cover desk:!overflow-hidden !overflow-visible w-full desk:!pb-40 !pb-32"
            :modules="[SwiperNavigation, SwiperPagination, SwiperA11y]"
            :space-between="8"
            :pagination="{
              clickable: true,
              dynamicBullets: true,
            }"
            :navigation="{
              nextEl: `.swiper-button-next.swiper-for-blok-${blok._uid}`,
              prevEl: `.swiper-button-prev.swiper-for-blok-${blok._uid}`,
            }"

            :loop="false"
            :slides-per-view="uiStore.isMobile ? 2.1 : 6"
            :no-swiping="true"
          >
            <SwiperSlide
              v-for="item in blok.listItems"
              :key="item._uid"
              class="flex flex-col items-center mb-24 desk:mb-32 deskLarge:!overflow-hidden desk:cursor-grab desk:active:cursor-grabbing"
            >
              <nuxt-link
                :to="itemLink(item)"
              >
                <div class="flex flex-col items-center group">
                  <div class="relative mb-12">
                    <nuxt-img
                      v-if="item.image && item.image.filename"
                      preset="standard"
                      loading="lazy"
                      sizes="75vw desk:600px"
                      class="aspect-portrait object-cover h-full w-full opacity-100 group-hover:opacity-80 transition-all duration-500 ease-out"
                      :class="item.focalPoint"
                      :src="item.image.filename"
                      :alt="item.image.alt"
                    />
                  </div>
                  <div class="type-headline-lg group-hover:underline underline-offset-4 decoration-1">
                    {{ item.category }}
                  </div>
                </div>
              </nuxt-link>
            </SwiperSlide>
          </Swiper>
        </client-only>
        <div class="hidden desk:block">
          <div
            class="swiper-button-prev !top-[140px]"
            :class="{[`swiper-for-blok-${blok._uid}`]: true}"
          />
          <div
            class="swiper-button-next !top-[140px]"
            :class="{[`swiper-for-blok-${blok._uid}`]: true}"
          />
        </div>
      </div>
      <div
        v-else-if="blok.design === 'images'"
        class="grid gap-x-8 gap-y-24"
        :class="[
          'grid-cols-[repeat(auto-fit,_minmax(150px,_1fr))] desk:grid-cols-[' + blok.listItems.length + ']'
        ]"
      >
        <nuxt-link
          v-for="item in blok.listItems"
          :key="item._uid"
          class="w-full"
          :to="itemLink(item)"
        >
          <div class="w-full flex flex-col items-center gap-12 group">
            <nuxt-img
              v-if="item.image && item.image.filename"
              preset="standard"
              loading="lazy"
              sizes="75vw desk:600px"
              class="aspect-portrait object-cover h-full w-full opacity-100 group-hover:opacity-80 transition-all duration-500 ease-out"
              :class="item.focalPoint"
              :src="item.image.filename"
              :alt="item.image.alt"
            />
            <div class="type-headline-lg text-center group-hover:underline underline-offset-4 decoration-1">
              {{ item.category }}
            </div>
          </div>
        </nuxt-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  StoryBlokPropBase,
  StoryBlokAsset,
  StoryBlokLink,
  ProductSelector,
  DatasourceObjectPosition
} from '~/constants/types/storyblok';

import { useUiStore } from '~/store/ui';
import { storeToRefs } from 'pinia';
import useVisualAppearancePlugin from '~/composeables/useVisualAppearancePlugin';
import SideScroll from '~/components/globals/SideScroll.vue';
import { useGlobalContentStore } from '~/store/globalContent';
const uiStore = useUiStore();
const { isMobile } = storeToRefs(uiStore);
const { $sbLinkFix, $sbLinkCheck } = useNuxtApp();
const globalContent = useGlobalContentStore();

type ListItem = StoryBlokPropBase & {
  category: string;
  image: StoryBlokAsset;
  link: StoryBlokLink;
  flagText: string;
  productLink: ProductSelector[]
  focalPoint: DatasourceObjectPosition;
}

type Props = StoryBlokPropBase & {
  title: string;
  textAlign: 'left' | 'center';
  swiperMobile: boolean;
  design: 'images' | 'images-center' | 'logo-images' | 'buttons' | 'buttons-swiper',
  logoImage: StoryBlokAsset,
  buttonText: string,
  link: StoryBlokLink,
  listItems: ListItem[];
// Heading fields
  headingTitle: string;
  headingButtonText: string;
  headingButtonLink: StoryBlokLink;
  headingButtonStyle: 'primary' | 'secondary' | 'underline';
  headingAlign: 'left' | 'center';
}

const props = defineProps<{
  blok: Props,
}>();

const { desktopWidth, blockPaddingTop, blockPaddingBottom } = useVisualAppearancePlugin(props);

const itemCount = computed(() => {
  if (!isMobile.value && (props.blok.design === 'images')) {
    return props.blok.listItems.length;
  } else {
    return 0;
  }
});

const itemLink = (item: ListItem) => {

  if (item.productLink &&
    item.productLink[0]?.product &&
    typeof item.productLink[0].product !== 'string' &&
    item.productLink[0].product.product !== null) {
    return `${globalContent.getPrependPathForManualLinks}/${globalContent.getProductBasePath}/${item.productLink[0].product.product.url}`;
  }
  if ($sbLinkCheck(item.link)) {
    return $sbLinkFix(item.link);
  }
  return null;
};

</script>
<style scoped lang="postcss">
.swiper-button-next {
  @apply !-right-16;
}
.swiper-button-prev {
  @apply !-left-16;
}
</style>
